import React, { useEffect } from 'react';
import { Spin, Tag } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import BoardCard from './BoardCard';
import { DATA_PORTAL_PAGES_BY_GROUP, DataPortalPageType } from '../graphql/queries';

interface Props {
    /** null 表示「未分组」这一段 */
    groupId: string | null;
    /**
     * 所在门户，只在 [groupId] 为 null（未分组段）时需要传：
     * 每个门户各有一段「未分组」，不带门户会让同一批看板在所有门户下重复出现。
     * [groupId] 非空时门户已由分组隐含，后端会忽略这个参数。
     */
    portalCode?: string;
    title: string;
    /** 组内已启用看板数，来自分组列表的聚合值；仅用于折叠态下的提示 */
    enabledCount?: number;
    expanded: boolean;
    onToggle: () => void;
    enabledFilter?: boolean;
    selectedIds: string[];
    onToggleSelect: (id: string) => void;
    onEdit: (page: DataPortalPageType) => void;
    onMove: (page: DataPortalPageType) => void;
    onDelete: (page: DataPortalPageType) => void;
    /** 让父组件感知已加载的看板（用于批量工具条的全选/计数） */
    onLoaded?: (pages: DataPortalPageType[]) => void;
    refreshToken: number;
}

/**
 * 管理页的一个分组段落。
 *
 * **展开才查该组**（`skip: !expanded`）：加载量与分组数而非看板总数成正比，
 * 几百个看板时也不会一次把全部卡片拉回来。
 */
const BoardGroupSection: React.FC<Props> = ({
    groupId,
    portalCode,
    title,
    enabledCount,
    expanded,
    onToggle,
    enabledFilter,
    selectedIds,
    onToggleSelect,
    onEdit,
    onMove,
    onDelete,
    onLoaded,
    refreshToken,
}) => {
    const { t } = useTranslation();

    const { data, loading, refetch } = useQuery<{ dataPortalPagesByGroup: DataPortalPageType[] }>(
        DATA_PORTAL_PAGES_BY_GROUP,
        {
            variables: { groupId, enabled: enabledFilter ?? null, portalCode: portalCode ?? null },
            skip: !expanded,
            // cache-first 而非 network-only：反复折叠/展开同一组时命中缓存，不重打这个慢接口。
            // 数据真变化（启停/移动/删除）时由下方 refreshToken 触发的 refetch() 强制刷新（refetch 走网络），
            // 所以「保证新鲜」已由 refreshToken 负责，fetchPolicy 不必再顶格用 network-only。
            fetchPolicy: 'cache-first',
            onCompleted: (result) => onLoaded?.(result?.dataPortalPagesByGroup ?? []),
            onError: (e) => console.warn('Fetch data portal pages by group failed', e),
        },
    );

    // 启停/移动/删除后由父组件递增 refreshToken，展开中的段落据此重查
    useEffect(() => {
        if (!expanded || refreshToken === 0) return;
        refetch().catch((e) => console.warn('Refetch data portal pages by group failed', e));
    }, [refreshToken, expanded, refetch]);

    const pages = data?.dataPortalPagesByGroup ?? [];

    return (
        <div className="mb-4">
            <div
                className="flex items-center gap-2 py-2 cursor-pointer text-sm text-gray-600"
                onClick={onToggle}
            >
                {expanded ? <DownOutlined className="text-xs" /> : <RightOutlined className="text-xs" />}
                <span className="font-medium">{title}</span>
                {groupId === null ? (
                    // 未分组段带橙色标签催归类，与原型一致
                    <Tag color="orange">{t('central.dataPortalConfig.pendingGroupCount', { count: enabledCount ?? 0 })}</Tag>
                ) : (
                    <span className="text-xs text-gray-400">· {enabledCount ?? 0}</span>
                )}
            </div>

            {expanded && (
                loading ? (
                    <div className="py-6 flex justify-center">
                        <Spin />
                    </div>
                ) : pages.length === 0 ? (
                    <div className="py-4 text-sm text-gray-400">{t('central.dataPortalConfig.emptyGroup')}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                        {pages.map((page) => (
                            <BoardCard
                                key={page.id}
                                page={page}
                                selected={selectedIds.includes(page.id)}
                                onToggleSelect={onToggleSelect}
                                onEdit={onEdit}
                                onMove={onMove}
                                onDelete={onDelete}
                            />
                        ))}
                    </div>
                )
            )}
        </div>
    );
};

export default BoardGroupSection;
